export type ItemFieldRow = Readonly<Record<string, unknown>> | readonly unknown[];

/** These are resolved reader results, not permission to query or compile a missing definition. */
export interface ItemFieldDependencies {
    resolveCarrierRef?: (id: string) => ItemFieldRow | null;
    resolveItemName?: (id: string) => unknown;
    resolveInventoryIdentity?: (id: string) => ItemFieldRow | null;
}

export class MissingItemFieldDependency extends Error {
    constructor(public readonly dependency: keyof ItemFieldDependencies, public readonly id: string) {
        super(`Missing item_field dependency: ${dependency}(${id})`);
        this.name = 'MissingItemFieldDependency';
    }
}

function arrayLike(value: unknown): value is ItemFieldRow {
    return value !== null && typeof value === 'object';
}
function has(value: unknown, key: string): boolean {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
}
function own(value: unknown, key: string): unknown {
    return has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
function collapse(value: unknown): unknown {
    return has(value, '0') ? own(value, '0') : value;
}
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index))
        ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
// Match check_uuid's raw PCRE input, including its single-final-LF boundary. No trimming.
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);

/** _ft_inventory_template_id: null coalescing occurs before the one-level array collapse. */
export function readInventoryTemplateId(row: ItemFieldRow | null): string | null {
    const value = own(own(row, 'items'), 'item_id')
        ?? own(own(row, 'inventory'), 'inventory_item_id') ?? own(row, 'inventory_item_id');
    const id = arrayLike(value) ? own(value, '0') : value;
    return validUuid(id) ? id : null;
}

/** Pure item_field decision tree over JSON rows. An unavailable external read throws;
 * an explicitly resolved null keeps PHP's ordinary missing-definition behavior.
 * Legacy-read diagnostics are intentionally outside this value reader.
 */
export function readItemField(row: ItemFieldRow | null, key: string, dependencies: ItemFieldDependencies = {},
    copyValue: (value: unknown) => unknown = clone): unknown {
    function lookup(name: keyof ItemFieldDependencies, id: string): unknown {
        const resolve = dependencies[name];
        if (!resolve) throw new MissingItemFieldDependency(name, id);
        return resolve(id);
    }
    function read(node: ItemFieldRow | null): unknown {
        if (node === null) return null;
        if (key === 'item_type') {
            const raw = own(own(node, 'encounter_creatures'), 'item_type');
            if ((arrayLike(raw) ? own(raw, '0') : raw) === 'lair') return 'lair';
        }
        const items = own(node, 'items');
        if (has(items, key)) return collapse(own(items, key));
        const identityKey = key === 'item_id' || key === 'item_type' || key === 'item_name';
        const pc = own(node, 'player_characters');
        if (identityKey && arrayLike(pc)) {
            if (key === 'item_type') return 'player_character';
            const value = collapse(own(pc, key === 'item_name' ? 'player_name' : 'item_id'));
            if (value != null && value !== '') return value;
        }
        if (key === 'item_type' && own(own(node, 'encounters'), 'encounter_id') != null) return 'encounter';
        if (identityKey && !has(node, key) && (own(node, 'status_arr') != null || own(node, 'status_item_id') != null)) {
            const status = own(node, 'status_arr') ?? own(node, 'status_item_id');
            if (arrayLike(status)) return read(status);
            if (validUuid(status)) {
                if (key === 'item_id') return status;
                const definition = lookup('resolveCarrierRef', status);
                if (arrayLike(definition)) return read(definition);
                return key === 'item_name' ? lookup('resolveItemName', status) : null;
            }
        }
        const inventoryId = own(own(node, 'inventory'), 'inventory_id');
        const legacyId = own(node, 'inventory_id');
        if ((identityKey || key === 'flavor_text' || key === 'item_created_by_id' || key === 'item_description' || key === 'homebrew')
            && !has(node, key)
            && ((inventoryId != null && inventoryId !== '') || (typeof legacyId === 'string' && legacyId !== ''))) {
            const id = readInventoryTemplateId(node);
            if (id !== null) {
                if (key === 'item_id') return id;
                const catalog = own(node, 'catalog');
                const riding = own(catalog, id) ?? own(catalog, id.toUpperCase()) ?? own(catalog, id.toLowerCase());
                if (arrayLike(riding)) {
                    const value = own(riding, key) ?? own(own(riding, 'items'), key);
                    // Riding catalog values collapse ANY array, unlike the ordinary own-field reader.
                    if (value != null) return arrayLike(value) ? own(value, '0') : value;
                }
                const identity = lookup('resolveInventoryIdentity', id);
                if (identity != null && (own(identity, key) ?? '') !== '') return own(identity, key);
            }
        }
        return has(node, key) ? collapse(own(node, key)) : null;
    }
    return copyValue(read(row));
}

/** The same field resolution for read-only traversal of a held native JSON tree.
 * Callers must not mutate the returned branch; ordinary readers retain PHP value copies. */
export function readItemFieldReference(row: ItemFieldRow | null, key: string): unknown {
    return readItemField(row, key, undefined, value => value ?? null);
}
